import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FolderIcon, 
  DocumentTextIcon, 
  UserGroupIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

/**
 * Dashboard for Content Supervisor role
 * Shows overview of KITs, questions, team assignments, and workflow status
 */
const ContentSupervisorDashboard = () => {
  const [stats, setStats] = useState({
    kits: { total: 0, draft: 0, published: 0, archived: 0 },
    questions: { total: 0, pending: 0, readyToShip: 0, withIssues: 0 },
    team: { generators: 0, reviewers: 0, activeAssignments: 0 }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch statistics
      const [kitStats, questionStats] = await Promise.all([
        api.get('/api/super-admin/kits/stats'),
        api.get('/api/super-admin/draft-questions/stats')
      ]);

      setStats({
        kits: kitStats.data,
        questions: questionStats.data,
        team: { generators: 5, reviewers: 3, activeAssignments: 12 } // Mock data for now
      });

      // Mock recent activity data
      setRecentActivity([
        { id: 1, type: 'question_reviewed', user: 'John Reviewer', action: 'Approved 5 questions', time: '2 hours ago' },
        { id: 2, type: 'kit_created', user: 'Jane Generator', action: 'Created new KIT: Physics MCQs', time: '4 hours ago' },
        { id: 3, type: 'assignment', user: 'System', action: 'Assigned 10 questions to Mike Reviewer', time: '6 hours ago' }
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const quickActions = [
    { name: 'Create New KIT', href: '/kits/create', icon: FolderIcon, color: 'bg-blue-500' },
    { name: 'Review Questions', href: '/draft-questions/for-publishing', icon: CheckCircleIcon, color: 'bg-green-500' },
    { name: 'Assign Work', href: '/assignments/create', icon: UserGroupIcon, color: 'bg-purple-500' },
    { name: 'View Analytics', href: '/analytics', icon: ChartBarIcon, color: 'bg-indigo-500' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Content Supervisor Dashboard</h1>
        <p className="text-gray-600 mt-1">Manage content workflow, KITs, and team assignments</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="group relative bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center">
              <div className={`${action.color} p-3 rounded-lg`}>
                <action.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                  {action.name}
                </h3>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* KIT Statistics */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">KITs Overview</h3>
            <FolderIcon className="h-8 w-8 text-blue-500" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total KITs</span>
              <span className="text-sm font-medium">{stats.kits.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Draft</span>
              <span className="text-sm font-medium text-yellow-600">{stats.kits.draft || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Published</span>
              <span className="text-sm font-medium text-green-600">{stats.kits.published || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Archived</span>
              <span className="text-sm font-medium text-gray-500">{stats.kits.archived || 0}</span>
            </div>
          </div>
        </div>

        {/* Question Statistics */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Questions Status</h3>
            <DocumentTextIcon className="h-8 w-8 text-green-500" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Questions</span>
              <span className="text-sm font-medium">{stats.questions.totalQuestions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Pending Review</span>
              <span className="text-sm font-medium text-yellow-600">{stats.questions.pendingQuestions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Ready to Ship</span>
              <span className="text-sm font-medium text-green-600">{stats.questions.readyToShipQuestions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">With Issues</span>
              <span className="text-sm font-medium text-red-600">
                {(stats.questions.renderingFailureQuestions || 0) + 
                 (stats.questions.duplicateQuestions || 0) + 
                 (stats.questions.notUsableQuestions || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Team Statistics */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Team Overview</h3>
            <UserGroupIcon className="h-8 w-8 text-purple-500" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Content Generators</span>
              <span className="text-sm font-medium">{stats.team.generators}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Content Reviewers</span>
              <span className="text-sm font-medium">{stats.team.reviewers}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active Assignments</span>
              <span className="text-sm font-medium text-blue-600">{stats.team.activeAssignments}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="p-6">
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {activity.type === 'question_reviewed' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    )}
                    {activity.type === 'kit_created' && (
                      <FolderIcon className="h-5 w-5 text-blue-500" />
                    )}
                    {activity.type === 'assignment' && (
                      <UserGroupIcon className="h-5 w-5 text-purple-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.user}</span> {activity.action}
                    </p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>

      {/* Workflow Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Workflow Alerts</h3>
          <div className="space-y-3">
            {stats.questions.pendingQuestions > 0 && (
              <div className="flex items-center space-x-2 text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                <ClockIcon className="h-5 w-5" />
                <span className="text-sm">
                  {stats.questions.pendingQuestions} questions pending review
                </span>
              </div>
            )}
            {(stats.questions.renderingFailureQuestions + stats.questions.duplicateQuestions + stats.questions.notUsableQuestions) > 0 && (
              <div className="flex items-center space-x-2 text-red-700 bg-red-50 p-3 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span className="text-sm">
                  {stats.questions.renderingFailureQuestions + stats.questions.duplicateQuestions + stats.questions.notUsableQuestions} questions need attention
                </span>
              </div>
            )}
            {stats.questions.readyToShipQuestions > 0 && (
              <div className="flex items-center space-x-2 text-green-700 bg-green-50 p-3 rounded-lg">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm">
                  {stats.questions.readyToShipQuestions} questions ready to publish
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Links</h3>
          <div className="space-y-2">
            <Link to="/kits" className="block text-sm text-blue-600 hover:text-blue-800">
              → Manage All KITs
            </Link>
            <Link to="/draft-questions" className="block text-sm text-blue-600 hover:text-blue-800">
              → View All Draft Questions
            </Link>
            <Link to="/users" className="block text-sm text-blue-600 hover:text-blue-800">
              → Manage Team Members
            </Link>
            <Link to="/analytics/content" className="block text-sm text-blue-600 hover:text-blue-800">
              → Content Analytics
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentSupervisorDashboard;
